import { useLayoutEffect, type RefObject } from "react";

export function gameViewportHeight(visualViewportHeight: number | undefined, innerHeight: number): number {
  const measured = visualViewportHeight && Number.isFinite(visualViewportHeight)
    ? visualViewportHeight
    : innerHeight;
  return Math.max(1, Math.round(measured));
}

/**
 * Keeps the arena inside the browser's visible viewport. Mobile Safari and
 * Chrome can report a layout viewport that extends behind their bottom bars;
 * VisualViewport is the source of truth while those controls animate.
 */
export function useGameViewport(root: RefObject<HTMLElement | null>): void {
  useLayoutEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const viewport = window.visualViewport;
    const htmlHadLock = html.classList.contains("yak-trails-page-lock");
    const bodyHadLock = body.classList.contains("yak-trails-page-lock");
    const previousHtmlHeight = html.style.getPropertyValue("--yak-trails-viewport-height");
    let animationFrame = 0;

    const update = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        const height = gameViewportHeight(viewport?.height, window.innerHeight);
        const value = `${height}px`;
        html.style.setProperty("--yak-trails-viewport-height", value);
        root.current?.style.setProperty("--yak-trails-viewport-height", value);
      });
    };

    html.classList.add("yak-trails-page-lock");
    body.classList.add("yak-trails-page-lock");
    viewport?.addEventListener("resize", update);
    viewport?.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    update();

    return () => {
      window.cancelAnimationFrame(animationFrame);
      viewport?.removeEventListener("resize", update);
      viewport?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      if (!htmlHadLock) html.classList.remove("yak-trails-page-lock");
      if (!bodyHadLock) body.classList.remove("yak-trails-page-lock");
      if (previousHtmlHeight) html.style.setProperty("--yak-trails-viewport-height", previousHtmlHeight);
      else html.style.removeProperty("--yak-trails-viewport-height");
    };
  }, [root]);
}
